import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Mail, Phone, Download, Filter, Shield, Wrench, Settings, Fish } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const StandardSuctionScreensPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What are Standard Suction Screens and how do they differ from self-cleaning models?",
      answer: "Standard Suction Screens are fixed intake filters with no moving parts or automatic cleaning mechanisms. They provide robust debris protection for surface or submerged pumping applications through a simple, reliable screen design. Unlike self-cleaning models (SCS2), standard screens require manual cleaning—typically by lifting the screen from the water and hosing/brushing debris off. Choose standard screens for: low-flow applications, systems with clean water sources, installations where manual maintenance is acceptable. Choose SCS2 for: high-debris environments, automated cleaning requirements, continuous operation without service interruptions."
    },
    {
      question: "How often do Standard Suction Screens require cleaning?",
      answer: "Cleaning frequency depends on water quality and debris loading. Clean water sources (wells, reservoirs): monthly to seasonally. Moderate debris (canals, ponds): weekly to monthly. Heavy organic matter (algae-prone ponds): daily to weekly. Monitor pump pressure or flow rate—clean when reduction is noticeable. Most cleaning takes 15-30 minutes: lift screen from water, hose off debris, reinstall. Replaceable screen elements extend service life and allow quick swaps during heavy debris seasons."
    },
    {
      question: "What materials are Standard Suction Screens available in?",
      answer: "Standard Suction Screens are available in mild steel with epoxy coating (economical, suitable for most irrigation applications) or 304/316 stainless steel (corrosive environments, marine applications, long service life). Screen elements: stainless steel perforated plate or woven mesh. Mild steel with epoxy is standard for agricultural irrigation in fresh water. Stainless steel is recommended for: coastal/brackish water, chlorinated water, food-grade applications, high-corrosion environments. Material selection depends on water chemistry and expected service life."
    },
    {
      question: "Can Standard Suction Screens handle sediment or silt?",
      answer: "Standard Suction Screens are designed primarily for coarse debris removal (leaves, twigs, algae, large organics). Fine sediment and silt can pass through the screen openings or accumulate on the screen surface. For silt-heavy water sources: consider larger screen surface area to reduce velocity and minimize clogging, use optional deflection cones to redirect sediment away from intake, clean more frequently during high-sediment periods. For applications requiring fine sediment removal, consider downstream filtration (BIG or TIG filters) or self-cleaning intake screens (SCS2) with spray bar cleaning."
    },
    {
      question: "How do I select the right model for my pump flow rate?",
      answer: "Match the screen model to your pump flow rate using the specifications table. Flow ranges are conservative—screens can handle higher flows but may require more frequent cleaning. General sizing: select screen with flow range equal to or greater than pump GPM, larger screens (longer screen length) provide more surface area and longer cleaning intervals, connection size (NPT or FLG) should match pump inlet size. Our filtration specialists can help size screens for specific water quality conditions, pump configurations, and maintenance intervals. For variable-flow pumps, size to maximum flow rate."
    },
    {
      question: "Can I retrofit a Standard Suction Screen to my existing pump system?",
      answer: "Yes, Standard Suction Screens are designed for easy installation on new or existing pump intakes. Match the connection type (NPT threaded or FLG flanged) to your pump inlet. Screens install directly on pump suction line or can be mounted on optional stands for canal/pond applications. For retrofit installations: verify adequate submersion depth for screen length, ensure screen won't contact pond/canal bottom (use stands if needed), check pump inlet size matches screen connection, consider adding check valve or foot valve for prime retention. Our filtration specialists can review your installation layout and recommend optimal mounting configuration."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Standard Suction Screens | Manual Intake Filters | CTC Waterworks"
        description="Standard Suction Screens for surface and submerged pumping. Robust intake protection with no moving parts. Fixed manual design for irrigation and industrial applications."
        canonical="https://www.ctcwaterworks.com/filtration/suction-screens/standard"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/suction-screens" className="text-gray-600 hover:text-ctc-blue">Suction Screens</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Standard Suction Screens</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Filter className="text-gray-700 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      Standard Suction Screens
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Robust intake protection for surface and submerged pumping.
                </p>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">About Standard Suction Screens</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Standard Suction Screens are robust intake filters for surface or submerged pumping applications. They prevent debris from entering the pump while offering a simple, maintenance-friendly design with no moving parts. Ideal for low-flow or manual-service systems where automatic cleaning is not required.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-gray-700 text-white px-6 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Standard Screen Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-gray-900 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose Standard Suction Screens?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-6">
              <Shield className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">No Moving Parts</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Simple fixed design with no actuators, motors, or controls. Reliable intake protection with minimal maintenance requirements and long service life.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Wrench className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Easy Maintenance</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Lift screen from water, hose off debris, reinstall. Typical service time 15-30 minutes. Replaceable screen elements for extended service life.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Filter className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Robust Construction</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Durable steel construction with replaceable screen elements. Available in epoxy-coated mild steel or stainless steel for corrosive environments.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Settings className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Versatile Applications</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Compatible with pump intakes, canal sumps, and pond applications. Surface or submerged mounting. Optional stands for sediment control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Image */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-center">
            <img
              src="/SCS2.jpeg"
              alt="Standard Suction Screen"
              className="max-w-full h-auto rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Standard Suction Screen Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Select model based on pump flow rate and connection type. All dimensions in inches.
          </p>

          {/* Size Diagram */}
          <div className="mb-6 flex justify-center">
            <img
              src="/SCS Size Diagram.png"
              alt="Standard Suction Screen Size Diagram"
              className="max-w-full h-auto"
              style={{ transform: 'rotate(90deg)', maxHeight: '300px' }}
            />
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">A (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">B (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">C (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">E (in)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">100</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">13</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">3 NPTM</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">200</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">15</td>
                  <td className="px-4 py-3">19</td>
                  <td className="px-4 py-3">4 NPTM</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">300</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">21</td>
                  <td className="px-4 py-3">5 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">400</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">6 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">600</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">23</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">8 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">800</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">33</td>
                  <td className="px-4 py-3">10 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1000</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">20</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">10 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1200</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">22</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">10 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1400</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">25</td>
                  <td className="px-4 py-3">33</td>
                  <td className="px-4 py-3">12 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1600</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">12 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">2000</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">42</td>
                  <td className="px-4 py-3">14 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">2400</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">16 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">3000</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">18 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">3600</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">20 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">4000</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">45</td>
                  <td className="px-4 py-3">59</td>
                  <td className="px-4 py-3">20 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">4500</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">54</td>
                  <td className="px-4 py-3">64</td>
                  <td className="px-4 py-3">22 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">5000</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">59</td>
                  <td className="px-4 py-3">69</td>
                  <td className="px-4 py-3">24 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">5500</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">64</td>
                  <td className="px-4 py-3">75</td>
                  <td className="px-4 py-3">24 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">6000</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">69</td>
                  <td className="px-4 py-3">81</td>
                  <td className="px-4 py-3">24 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">6500</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">74</td>
                  <td className="px-4 py-3">86</td>
                  <td className="px-4 py-3">30 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">7000</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">79</td>
                  <td className="px-4 py-3">91</td>
                  <td className="px-4 py-3">30 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">7500</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">84</td>
                  <td className="px-4 py-3">97</td>
                  <td className="px-4 py-3">30 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">8000</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">89</td>
                  <td className="px-4 py-3">102</td>
                  <td className="px-4 py-3">30 FLG</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">8500</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">94</td>
                  <td className="px-4 py-3">108</td>
                  <td className="px-4 py-3">36 FLG</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Materials:</span> Available in mild steel with epoxy coating or 304/316 stainless steel options. All dimensions in inches. NPT = National Pipe Thread (threaded connection), FLG = Flanged connection. Screen elements: stainless steel perforated plate or woven mesh. Contact our filtration specialists for material selection guidance based on water chemistry and application requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Features</h2>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <ul className="space-y-4">
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-gray-700 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Durable steel construction with replaceable screen elements</span>
                  <p className="text-gray-700 text-sm mt-1">Robust design for long service life. Screen elements can be replaced without replacing entire assembly—reduces long-term costs.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-gray-700 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Fixed manual design (no power or actuation required)</span>
                  <p className="text-gray-700 text-sm mt-1">Simple, reliable operation with no electrical or mechanical controls. No power consumption or automation complexity.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-gray-700 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Coarse-screen intake protection for irrigation and industrial pumps</span>
                  <p className="text-gray-700 text-sm mt-1">Prevents large debris (leaves, twigs, algae) from entering pump. Protects pump impellers and extends pump service life.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-gray-700 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Compatible with pump intakes, canal sumps, and pond applications</span>
                  <p className="text-gray-700 text-sm mt-1">Versatile mounting options: direct pump connection, surface mounting, or submerged installation with optional stands.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-gray-700 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Optional deflection cones or stands for sediment control</span>
                  <p className="text-gray-700 text-sm mt-1">Accessories available to redirect sediment flow and elevate screen above pond/canal bottom. Contact our filtration specialists for recommendations.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-6 bg-gray-50 border-l-4 border-gray-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Installation Note:</span> Standard Suction Screens install directly on pump suction line or can be mounted on optional stands for canal/pond applications. Ensure adequate submersion depth for screen length plus 6-12 inches clearance. Screen should not contact bottom to avoid sediment accumulation. For specific installation guidance, contact CTC filtration specialists with your site conditions and pump configuration.
            </p>
          </div>
        </div>
      </section>

      {/* Fish Compliance Section */}
      <section className="py-16 bg-green-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="flex items-start">
              <Fish className="text-green-600 mr-4 flex-shrink-0 mt-1" size={32} />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Fish-Compliant Intake Protection</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Standard Suction Screens are fish-compliant when properly sized and configured. The fixed screen design with appropriate perforation sizes (typically 3/32" or smaller) and adequate screen surface area maintains low approach velocities that meet regulatory requirements for fish protection.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our filtration specialists can calculate approach velocities and provide documentation for permit applications to ensure your screen meets local, state, and federal fish protection standards including WDFW, NOAA Fisheries (NMFS), and other regulatory agencies.
                </p>
                <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <span className="font-semibold text-gray-900">Compliance Note:</span> Proper sizing is critical for fish compliance. Larger screen surface area reduces approach velocity. Contact our filtration specialists with your flow requirements and local regulatory standards for compliant screen sizing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Reference */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Reference</h2>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                  <th className="px-4 py-3 text-left font-semibold">Open Area (%)</th>
                  <th className="px-4 py-3 text-left font-semibold">Equivalent Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Typical Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">0.020–0.025</td>
                  <td className="px-4 py-3">Intake</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.015–0.020</td>
                  <td className="px-4 py-3">Micro</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">80</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.010–0.012</td>
                  <td className="px-4 py-3">Drip</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.008–0.010</td>
                  <td className="px-4 py-3">Fine Emitter Protection</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Compare Suction Screen Options</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/filtration/suction-screens/scs2"
              className="bg-white border-2 border-ctc-blue rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Self-Cleaning Suction Screens (SCS2)</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For automated cleaning and high-debris environments, consider SCS2 Self-Cleaning Suction Screens. Spray bar system cleans screen without lifting from water.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare Self-Cleaning Options
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/suction-screens/psf"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Passive Fish Screens (PSF)</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For fish-compliant water intakes meeting NOAA/NMFS standards, view PSF Passive Fish Screens. Wedge-wire construction with passive fish protection.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View Passive Fish Screens
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-gray-700 transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-700 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Pump with Standard Suction Screens?</h2>
          <p className="text-xl text-gray-200 mb-8">
            Our filtration specialist team will help you select the right model, recommend optimal screen configuration, and design a complete intake protection system for your pumping application.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My System
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-200">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StandardSuctionScreensPage;
